/**
 * 読み込めなかった・行えなかったときの「理由」と「次にしていただくこと」（総点検 J-08）。
 *
 * 次にしていただくことは、**失敗の種類で変える**。権限が無いとき・見つからないときに
 * 「少し時間をおいて、もう一度お試しください」と出すと、待っても直らないのに待たせてしまうため。
 * 画面の部品（components/StatusBlock.tsx の ErrorBlock）と、各画面の結果の帯の両方から使う。
 */
import { ApiError, messageForStatus } from './client.js';

/** 読み込めなかった理由（画面に出す言葉と、失敗の種類）。 */
export interface LoadFailure {
  message: string;
  /** HTTP の状態番号（つながらなかったときは 0） */
  status: number;
  /** やり直せば通る見込みがあるか（サーバが返したとき。無ければ状態番号で見分ける） */
  retryable?: boolean;
}

/** 受け取った例外から、画面に出せる形を作る。 */
export function loadFailure(caught: unknown): LoadFailure {
  if (caught instanceof ApiError) {
    return {
      message: caught.message,
      status: caught.status,
      ...(caught.retryable !== undefined ? { retryable: caught.retryable } : {}),
    };
  }
  return { message: messageForStatus(0), status: 0 };
}

/**
 * うまくいかなかったときに、次にしていただくこと（待てば直りうる失敗）。
 * 頼み先は、ほかの画面（ログインの入口など）と同じ「システムの担当者」にそろえる（#334 L-1）。
 */
export const DEFAULT_NEXT_STEP =
  '少し時間をおいて、もう一度お試しください。それでも直らないときは、システムの担当者へお知らせください。';

/**
 * 理由の文に、もう次の一手（「もう一度お試しください」など）が書いてあるか。
 * 書いてあるときは、画面の側で同じお願いを重ねない（#334 L-1。同じお願いが2回続かないように）。
 */
export function mentionsNextStep(message: string): boolean {
  return /お試しください|ご確認ください|ご依頼ください|お知らせください|開き直してください/.test(
    message,
  );
}

/**
 * 権限が無いとき。待っても直らないので、頼み先を書く。
 * どの画面・どの操作で断られたか分からない一般の案内なので、役職の呼び名は出さない
 * （初期の呼び名を決め打ちしない・issue #480）。
 */
export const FORBIDDEN_NEXT_STEP =
  'この画面は、いまの役職ではお使いいただけません。必要なときは、ほかの管理者の方へご依頼ください。';

/** 見つからないとき（消された・受け持ちの範囲の外）。 */
export const NOT_FOUND_NEXT_STEP = '一覧に戻って、開き直してください。';

/** 待っても直らないが、種類の分からない失敗（内容の誤りなど）。 */
const NOT_RETRYABLE_NEXT_STEP = '画面を開き直して、内容をご確認ください。';

/**
 * 失敗の種類ごとの、次にしていただくこと。待っても直らない失敗では読み込み直しを出さない。
 * サーバが `retryable: false` を返したときは、状態番号にかかわらず「時間をおいて」を出さない。
 */
export function nextStepFor(
  status: number,
  retryable?: boolean,
): { nextStep: string; retryable: boolean } {
  if (status === 403) return { nextStep: FORBIDDEN_NEXT_STEP, retryable: false };
  if (status === 404) return { nextStep: NOT_FOUND_NEXT_STEP, retryable: false };
  if (retryable === false) return { nextStep: NOT_RETRYABLE_NEXT_STEP, retryable: false };
  return { nextStep: DEFAULT_NEXT_STEP, retryable: true };
}

/**
 * 操作が断られたときの次の一手。やり直せば通る見込みがあるときだけ `whenRetryable`
 * （「少し時間をおいて…」など）を出し、そうでないとき（権限が無い・名簿で決まっている など）は
 * 待たせない言い方にする（総点検 J-08・サーバの `retryable`）。
 */
export function nextStepAfter(caught: unknown, whenRetryable: string): string {
  const failure = loadFailure(caught);
  const guide = nextStepFor(failure.status, failure.retryable);
  return guide.retryable ? whenRetryable : guide.nextStep;
}
